import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Dumbbell, Clock, Zap, History, Trash2 } from 'lucide-react';
import BottomNav from '../components/BottomNav';

const getIntensityColor = (intensity) => {
    switch (intensity?.toLowerCase()) {
        case 'low': return 'bg-green-500';
        case 'medium': return 'bg-yellow-500';
        case 'high': return 'bg-red-500';
        case 'stretching': return 'bg-blue-500';
        default: return 'bg-gray-500';
    }
};

const MyExercisePage = ({ myExercises, removeMyExercise }) => {
    const navigate = useNavigate();
    const [deleteId, setDeleteId] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const handleDelete = async (exerciseId) => {
        setDeleting(true);
        try {
            await removeMyExercise(exerciseId);
            setSuccessMessage('운동이 삭제되었습니다.');
            setTimeout(() => setSuccessMessage(''), 3000);
            setDeleteId(null);
        } catch (err) {
            console.error('Failed to delete exercise:', err);
            alert('운동 삭제에 실패했습니다.');
        } finally {
            setDeleting(false);
        }
    };


    return (
        <div className="flex flex-col min-h-screen bg-gray-100 pb-20">
            <header className="bg-white shadow-sm sticky top-0 z-10">
                <div className="max-w-4xl mx-auto p-4 flex items-center justify-center relative">
                    <button onClick={() => navigate('/')} className="absolute left-4">
                        <ArrowLeft className="w-6 h-6 text-gray-700" />
                    </button>
                    <h1 className="text-xl font-bold text-gray-800">내 운동 목록</h1>
                </div>
            </header>

            {successMessage && (
                <div style={{
                    position: 'fixed',
                    top: '80px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    backgroundColor: '#10b981',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    zIndex: 50,
                }}>
                    {successMessage}
                </div>
            )}

            <main className="flex-grow p-4">
                <div className="max-w-4xl mx-auto">
                    {myExercises && myExercises.length > 0 ? (
                        <div className="space-y-4">
                            {myExercises.map((exercise) => (
                                <div
                                    key={exercise.exercise_id}
                                    className="bg-white rounded-xl p-6 shadow-md border border-gray-200 relative"
                                >
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDeleteId(exercise.exercise_id);
                                        }}
                                        className="absolute top-4 right-4 p-2 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <Trash2 className="w-5 h-5 text-red-500" />
                                    </button>

                                    <div className="flex items-start justify-between mb-4 pr-10">
                                        <div className="flex-1">
                                            <h3 className="text-xl font-bold text-gray-900 mb-1">{exercise.name}</h3>
                                            <p className="text-gray-600 text-sm">{exercise.description}</p>
                                        </div>
                                        <Dumbbell className="w-8 h-8 text-indigo-500 flex-shrink-0 ml-4" />
                                    </div>

                                    <div className="grid grid-cols-3 gap-4 mb-4">
                                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                                            <Clock className="w-4 h-4 text-gray-500 mx-auto mb-1" />
                                            <p className="text-lg font-semibold">{exercise.duration_minutes}분</p>
                                        </div>
                                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                                            <Zap className="w-4 h-4 text-gray-500 mx-auto mb-1" />
                                            <div className="flex items-center justify-center gap-2">
                                                <span className={`w-2 h-2 rounded-full ${getIntensityColor(exercise.intensity)}`}></span>
                                                <p className="text-lg font-semibold capitalize">{exercise.intensity}</p>
                                            </div>
                                        </div>
                                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                                            <span className="text-xs text-gray-500 block mb-1">세트×반복</span>
                                            <p className="text-lg font-semibold">{exercise.sets} × {exercise.repetitions}</p>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => navigate(`/exercise/${exercise.exercise_id}`)}
                                        className="w-full mt-2 bg-indigo-600 hover:bg-indigo-700 text-white py-3 rounded-lg font-semibold transition"
                                    >
                                        이 운동 시작하기 →
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 px-4">
                            <History className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <h3 className="text-xl font-bold text-gray-800 mb-2">저장된 운동이 없습니다</h3>
                            <p className="text-gray-600 mb-6">
                                AI 추천을 통해 나만의 운동 목록을 만들어보세요.
                            </p>
                            <button
                                onClick={() => navigate('/exercise-selection')}
                                className="bg-[#4CAF50] hover:bg-[#499c4c] text-white px-6 py-3 rounded-lg font-semibold"
                            >
                                운동 추천 받으러 가기
                            </button>
                        </div>
                    )}
                </div>
            </main>

            {/* 삭제 확인 모달 */}
            {deleteId && (
                <div style={{
                    position: 'fixed',
                    inset: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 50,
                    padding: '16px'
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '16px',
                        padding: '24px',
                        maxWidth: '400px',
                        width: '100%'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '48px',
                            height: '48px',
                            backgroundColor: '#fee2e2',
                            borderRadius: '50%',
                            margin: '0 auto 16px'
                        }}>
                            <Trash2 style={{ width: '24px', height: '24px', color: '#dc2626' }} />
                        </div>
                        <h3 style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#111827',
                            textAlign: 'center',
                            marginBottom: '8px'
                        }}>
                            운동을 삭제하시겠습니까?
                        </h3>
                        <p style={{
                            color: '#6b7280',
                            textAlign: 'center',
                            marginBottom: '8px',
                            fontSize: '14px'
                        }}>
                            이 운동 템플릿이 삭제됩니다.
                        </p>
                        <p style={{
                            color: '#3b82f6',
                            textAlign: 'center',
                            marginBottom: '24px',
                            fontSize: '13px',
                            fontWeight: '500'
                        }}>
                            ※ 이미 완료한 운동 기록은 유지됩니다
                        </p>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <button
                                onClick={() => setDeleteId(null)}
                                disabled={deleting}
                                style={{
                                    flex: 1,
                                    padding: '12px 16px',
                                    backgroundColor: '#e5e7eb',
                                    color: '#374151',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    border: 'none',
                                    cursor: deleting ? 'not-allowed' : 'pointer',
                                    opacity: deleting ? 0.5 : 1
                                }}
                            >
                                취소
                            </button>
                            <button
                                onClick={() => handleDelete(deleteId)}
                                disabled={deleting}
                                style={{
                                    flex: 1,
                                    padding: '12px 16px',
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    borderRadius: '8px',
                                    fontWeight: '600',
                                    border: 'none',
                                    cursor: deleting ? 'not-allowed' : 'pointer',
                                    opacity: deleting ? 0.5 : 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                {deleting ? (
                                    <>
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            border: '2px solid white',
                                            borderTopColor: 'transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite'
                                        }}></div>
                                        삭제 중...
                                    </>
                                ) : (
                                    '삭제'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <BottomNav active="my-exercise" />
        </div>
    );
};

export default MyExercisePage;